/**
 * AIInventory-local design tokens.
 *
 * Composed from `styles/colors`, `styles/variables`, `styles/typography`, and
 * `styles/spacing` so the feature inherits the product palette. Only items
 * with AIInventory-specific semantics live here — platform accents, motion
 * timings, chip tones, and scoped keyframes in THEME_GLOBAL_STYLES.
 */
import type { AIPlatform } from "@p0security/shared/types/ai-inventory";
import { colors as globalColors } from "styles/colors";
import { spacing } from "styles/spacing";
import { typography } from "styles/typography";
import { colors as variables } from "styles/variables";

export const color = {
  card: globalColors.fill.surface.light,
  mute: variables.neutral["10"],
  rule: globalColors.border.deemphasis.light,
  ruleStrong: variables.neutral["20"],

  ink: globalColors.text.emphasis.light,
  inkDim: variables.neutral["60"],
  inkMute: globalColors.text.deemphasis.light,

  // managed = routes through P0 gateway; inherits the brand teal
  integration: globalColors.brand.light,
  integrationBg: globalColors.fill.background.light,
  integrationBgDeep: variables.themeColors.lightGreen,

  // P0 MCP control plane
  controlPlane: variables.primary["50"],
  controlPlaneBg: variables.primary["00"],
  controlPlaneInk: variables.primary["100"],

  // direct-connector warning (not routed through P0)
  unmanaged: variables.tagColors.orange.text,
  unmanagedBg: variables.tagColors.yellow.bg,

  // trend accents
  owner: globalColors.dataViz.viz3.light,
  agentsConnectors: variables.primary["50"],

  // risk ladder
  riskUrgent: globalColors.status.critical.light,
  riskUrgentBg: variables.tagColors.red.bg,
  riskHigh: globalColors.status.error.light,
  riskHighBg: variables.tagColors.orange.bg,
  riskMedium: globalColors.status.warning.light,
  riskMediumBg: variables.tagColors.yellow.bg,
  riskLow: variables.neutral["50"],
  riskLowBg: variables.neutral["05"],
} as const;

/** Ribbon accent per platform. */
export const platformAccent: Record<AIPlatform, string> = {
  "copilot-studio": variables.primary["50"],
  bedrock: variables.tagColors.orange.text,
  "local-agents": globalColors.dataViz.viz1.light,
};

export const font = {
  sans: typography.fontFamily.body,
  mono: typography.fontFamily.code,
} as const;

/** Tabular-nums for numeric-heavy surfaces. */
export const numericStyle: React.CSSProperties = {
  fontVariantNumeric: "tabular-nums",
};

export const radius = {
  sharp: spacing.radius.sm,
  card: spacing.radius.md,
} as const;

export type ChipTone = "amber" | "blue" | "green" | "neutral" | "red";

export const toneColors: Record<ChipTone, { bg: string; fg: string }> = {
  green: { bg: color.integrationBg, fg: color.integration },
  amber: { bg: color.unmanagedBg, fg: color.unmanaged },
  red: { bg: color.riskUrgentBg, fg: color.riskUrgent },
  blue: { bg: color.mute, fg: color.agentsConnectors },
  neutral: { bg: color.mute, fg: color.inkMute },
};

export const iconTile = (tone: ChipTone): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 18,
  height: 18,
  borderRadius: radius.sharp,
  background: toneColors[tone].bg,
  color: toneColors[tone].fg,
  fontSize: 11,
  flexShrink: 0,
});

export const motion = {
  fast: "120ms cubic-bezier(.2,.8,.2,1)",
  base: "200ms cubic-bezier(.2,.8,.2,1)",
  slow: "360ms cubic-bezier(.2,.8,.2,1)",
} as const;

// Derived from global brand teal for the live-pulse glow.
const LIVE_PULSE_RGB = "0, 171, 142";

/** Scoped keyframes + hover classes. Rendered once from the AIInventory root. */
export const THEME_GLOBAL_STYLES = `
@keyframes p0-live-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(${LIVE_PULSE_RGB}, 0.45); }
  70%  { box-shadow: 0 0 0 5px rgba(${LIVE_PULSE_RGB}, 0); }
  100% { box-shadow: 0 0 0 0 rgba(${LIVE_PULSE_RGB}, 0); }
}

@keyframes p0-chip-enter {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: none; }
}

@keyframes p0-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.p0-agent-row {
  border-top: 1px solid ${color.rule};
  border-right: 1px solid ${color.rule};
  border-bottom: 1px solid ${color.rule};
  transition: border-color ${motion.base}, box-shadow ${motion.base};
}
.p0-agent-row:hover {
  border-top-color: ${color.ruleStrong};
  border-right-color: ${color.ruleStrong};
  border-bottom-color: ${color.ruleStrong};
  box-shadow: 0 2px 16px -12px rgba(0,0,0,0.24);
}
.p0-agent-row__actions {
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  gap: 8px;
}

.p0-bar-seg {
  cursor: default;
  transition: filter ${motion.fast};
}
.p0-bar-seg:hover {
  filter: brightness(1.15);
}

.p0-secure-pill:hover {
  filter: brightness(0.92);
  transform: translateY(-1px);
}
.p0-secure-pill:active {
  filter: brightness(0.85);
  transform: translateY(0);
}
.p0-secure-pill:focus-visible {
  outline: 2px solid rgba(0,0,0,0.18);
  outline-offset: 2px;
}

@media (max-width: 1280px) {
  .p0-agent-row .p0-secure-pill__label {
    display: none;
  }
}
`;
